import React from 'react';
import { Autocomplete, Box, Chip, FormLabel, Stack, TextField, Typography } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';

const CHIP_COLOR = '#1976d2';

const selectableChipSx = (selected, color = CHIP_COLOR) => ({
    bgcolor: selected ? color : 'transparent',
    color: selected ? '#fff' : color,
    border: `1px solid ${color}`,
    fontWeight: 500,
    cursor: 'pointer',
    '&:hover': { bgcolor: selected ? color : `${color}1a` },
});

const ChipGroup = ({ id, label, required, options, selectedValue, onSelect, error, errorText, emptyText }) => (
    <Box>
        <FormLabel component="legend" required={required} error={error} sx={{ fontSize: '0.85rem', mb: 0.75, display: 'block' }}>
            {label}
        </FormLabel>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {options.length === 0 ? (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                    {emptyText}
                </Typography>
            ) : (
                options.map((opt) => {
                    const selected = selectedValue === opt;
                    return (
                        <Chip
                            key={opt}
                            label={opt}
                            onClick={() => onSelect({ target: { id, value: selected && !required ? '' : opt } })}
                            sx={selectableChipSx(selected)}
                        />
                    );
                })
            )}
        </Box>
        {error && (
            <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, display: 'block' }}>
                {errorText}
            </Typography>
        )}
    </Box>
);

const OtherDetailsCard = (props) => {
    const designation = props.designation || [];
    const contactType = props.contactType || [];
    const contactStatus = props.contactStatus || [];

    let customerList = (props.customerList || []).map((elem) => ({ value: elem.id, title: elem.customer_name }));
    customerList = [{ value: 'NULL', title: 'Not Related to Any Customer' }, ...customerList];

    const sub = props.submitAttempted;
    const errCustomer = sub && !props.formVals.customer;
    const errStatus = sub && !props.formVals.status;
    const errType = sub && !props.formVals.contact_type;

    return (
        <Stack spacing={2}>
            <ChipGroup
                id="designation"
                label="Designation (Optional)"
                options={designation}
                selectedValue={props.formVals.designation}
                onSelect={props.formChangeHandler}
                emptyText="No designations available"
            />

            <Autocomplete
                id="customer"
                fullWidth
                size="small"
                value={customerList.find((type) => type.value === props.formVals.customer) || null}
                onChange={(e, newValue) => {
                    const val = customerList.find((man) => man.title === (newValue && newValue.title));
                    props.formChangeHandler({ target: { id: 'customer', value: val ? val.value : '' } });
                }}
                disabled={props.edit}
                options={customerList}
                getOptionLabel={(option) => option.title}
                renderInput={(params) => (
                    <TextField {...params} label="Related Customer" variant="outlined" required error={errCustomer} helperText={errCustomer ? 'Related customer is required' : ''} />
                )}
            />

            <ChipGroup
                id="status"
                label="Contact Status"
                required
                options={contactStatus}
                selectedValue={props.formVals.status}
                onSelect={props.formChangeHandler}
                error={errStatus}
                errorText="Contact status is required"
                emptyText="No statuses available"
            />

            <ChipGroup
                id="contact_type"
                label="Contact Type"
                required
                options={contactType}
                selectedValue={props.formVals.contact_type}
                onSelect={props.formChangeHandler}
                error={errType}
                errorText="Contact type is required"
                emptyText="No contact types available"
            />

            <Box sx={{ display: 'flex', gap: 1.5 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                        label="First Contact Date"
                        value={new Date(moment(props.formVals.first_contact_date).format('YYYY-MM-DDTHH:mm:ss'))}
                        onChange={(d) => props.formChangeHandler({ target: { value: moment(d).format('YYYY-MM-DDTHH:mm:ss'), id: 'first_contact_date' } })}
                        slotProps={{ textField: { variant: 'outlined', size: 'small', fullWidth: true } }}
                    />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                        label="Last Contact Date"
                        value={new Date(moment(props.formVals.last_contact_date).format('YYYY-MM-DDTHH:mm:ss'))}
                        onChange={(d) => props.formChangeHandler({ target: { value: moment(d).format('YYYY-MM-DDTHH:mm:ss'), id: 'last_contact_date' } })}
                        slotProps={{ textField: { variant: 'outlined', size: 'small', fullWidth: true } }}
                    />
                </LocalizationProvider>
            </Box>

            <TextField
                id="con_note"
                label="Notes (Optional)"
                multiline
                minRows={4}
                fullWidth
                size="small"
                value={props.formVals.con_note || ''}
                onChange={(e) => props.formChangeHandler({ target: { id: 'con_note', value: e.target.value } })}
                variant="outlined"
            />
        </Stack>
    );
};

export default OtherDetailsCard;
